import { isEqual } from "lodash-es";
import type { IAnyObject } from "~/typings/object";
import type { IGoodsFilter, IGoodsFilters, IRequestGoodsFilters } from "~/typings/api/goods";
import type { ICategory } from "~/typings/categories";

export default (
  catalogType: "goods" | "promo",
  filters?: IGoodsFilters | null | Ref<IGoodsFilters | null>,
) => {
  const route = useRoute();
  const router = useRouter();
  const categoriesStore = useCategoriesStore();

  const categoryId = computed(() => (route.params.category || "") as string);

  const currentCategory = computed<ICategory | null>(() =>
    categoriesStore[catalogType].length
      ? categoriesStore.getCategoryById(categoryId.value.toString(), catalogType)
      : null,
  );

  const getFiltersFromUrlParams = () => {
    const res: IAnyObject = {};
    Object.entries(route.query).forEach(([key, value]) => {
      const _filter = toValue(filters)?.filters.find((i) => i.id === key);
      if (_filter) {
        switch (_filter.type) {
          case "grid":
            res[key] = Array.isArray(value) ? value : [value];
            break;
          case "range":
            if (Array.isArray(value)) {
              const _values = value.map((v) => Number(v));
              if (_values.filter((v) => !isNaN(v)).length !== 2) {
                return;
              }
              res[key] = {
                min: Math.min(..._values),
                max: Math.max(..._values),
              };
            }
            break;
        }
      }
    });
    if (route.query?.onlyDiscount === "true") {
      res.onlyDiscount = true;
    }
    return res;
  };

  const getFiltersPayload = (filters: IGoodsFilter[], value: IAnyObject) => {
    const res: Partial<IRequestGoodsFilters> = {
      filters: [],
    };
    if (value.onlyDiscount) {
      res.onlyDiscount = value.onlyDiscount;
    }

    Object.entries(value).forEach(([key, value]) => {
      const filter = filters.find((i) => i.id === key);
      switch (filter?.type) {
        case "grid":
          if (toRaw(value).length) {
            res.filters?.push({
              elements: toRaw(value),
              id: key,
            });
          }
          break;
        case "range":
          if (
            !isEqual(
              {
                min: filter?.minValue,
                max: filter?.maxValue,
              },
              toRaw(value),
            )
          ) {
            res.filters?.push({
              id: key,
              range: toRaw(value),
            });
          }
          break;
      }
    });
    if (!res.filters?.length) {
      delete res.filters;
    }
    return res;
  };

  const getArrayFilters = (filters: IGoodsFilter[]): [string, [] | { min: number; max: number; } | null][] =>
    filters.map((i) => [
      i.id,
      i.type === "grid"
        ? []
        : i.type === "range"
          ? {
              min: i.minValue,
              max: i.maxValue,
            }
          : null,
    ]);

  const arrayFilters = getArrayFilters(toValue(filters)?.filters || []);

  const defaultStaticsFilters = {
    onlyDiscount: false,
    categoryKey: currentCategory.value?.key,
  };

  const defaultFilters = ref({
    ...Object.fromEntries(arrayFilters || []),
    ...defaultStaticsFilters,
  });

  const innerFilters = ref({
    ...Object.fromEntries(arrayFilters || []),
    ...defaultStaticsFilters,
    ...getFiltersFromUrlParams(),
  });
  const diffFilters = computed<IAnyObject>(() =>
    shallowDiff(toValue(innerFilters), toValue(defaultFilters)),
  );
  const countChangeFilters = computed(() => Object.keys(diffFilters.value).length);

  watch(
    innerFilters,
    () => {
      const _filters: IAnyObject = {};
      const { onlyDiscount, categoryKey, ...otherFilters } = diffFilters.value;
      if (onlyDiscount) {
        _filters.onlyDiscount = onlyDiscount;
      }
      Object.entries(otherFilters).forEach(([key, value]) => {
        const type = toValue(filters)?.filters.find((i) => i.id === key)?.type;
        switch (type) {
          case "grid":
            if (toRaw(value).length) {
              _filters[key] = toRaw(value);
            }
            break;
          case "range":
            _filters[key] = [value.min, value.max];
            break;
        }
      });

      const saveRouteQuery = Object.fromEntries(
        Object.entries(route.query).filter(([key]) =>
          ["sortBy", "order", "shopCode"].includes(key),
        ),
      );

      const category = categoriesStore.getCategoryByKey(categoryKey);
      router.push({
        path: category?.url || route.path,
        query: { ...saveRouteQuery, ..._filters },
      });
    },
    {
      deep: true,
    },
  );
  const onResetFilters = () => {
    innerFilters.value = { ...defaultFilters.value };
  };

  return {
    getFiltersFromUrlParams,
    getFiltersPayload,
    getArrayFilters,
    countChangeFilters,
    onResetFilters,
    innerFilters,
    defaultFilters,
    currentCategory,
  };
};
